import { textColorFields } from '../_shared/textColor.js';
import { animationFields } from '../_shared/universal.js';
import { paymentMethodFields } from '../../blocks/_shared/paymentIcons.js';
/**
 * footer — the site footer (a GLOBAL section on the 'footer' template), rebuilt to Shopify/Shrine parity:
 * a 12-column BLOCK GRID (content-top) of `brand_information` / `link_list` / `text` / `email_signup` /
 * `image` blocks, then a content-bottom row (payment + copyright + branding). Each block carries
 * `width_desktop` (1–12) + `width_mobile` (half|full).
 *
 * THE MENU MODEL (the headline change): a `link_list` block no longer authors links inline — it PICKS one
 * of the store's named menus (defined once in dashboard → Navigation) via the `menu` field (stores the
 * handle). The footer renders that menu's items at render via `RenderContext.resolved.menus[handle]`,
 * resolving each item's url through `resolveUrl` (the same path the header uses). Footer renders level 1.
 *
 * Social links are section-level per-platform URL fields, rendered in the `brand_information` block (Shrine
 * puts social at the theme level; footer-level is the aquilixShop equivalent until a theme-settings store
 * exists). The pattern-background engine + the light/inverse/accent scheme are kept. The footer is
 * full-bleed chrome, so it carries its OWN scheme toggle. Verbatim Shrine `base.css` spacing/typography is
 * ported in the Component's scoped stylesheet (rem×10→px), colors remapped to the `--aqx-scheme-*` tokens.
 *
 * Back-compat: the Component also renders LEGACY v1 blocks (`column` with a `Label|url` textarea, `social`
 * with an icon+url) so an old footer keeps working; new authoring uses the blocks below. version 1→2.
 */
const PATTERN_FIELDS = [
    {
        id: 'patternBg',
        type: 'select',
        label: 'Background pattern',
        group: 'Background pattern',
        default: 'none',
        options: [
            { value: 'none', label: 'None' },
            { value: 'dotted', label: 'Dots' },
            { value: 'grid', label: 'Grid' },
            { value: 'curved-grid', label: 'Curved grid' },
            { value: 'diagonal-stripes', label: 'Stripes' },
            { value: 'zigzag', label: 'ZigZag' },
            { value: 'arrow-pattern', label: '3D Boxes' },
            { value: 'radial-rings', label: 'Rings' },
            { value: 'diamond-grid-1', label: 'Diamond grid 1' },
            { value: 'diamond-grid-2', label: 'Diamond grid 2' },
            { value: 'quatrefoils', label: 'Quatrefoils' },
            { value: 'ovals', label: 'Ovals' },
            { value: 'hexagons', label: 'Hexagons' },
            { value: 'curvy', label: 'Curvy' },
        ],
    },
    {
        id: 'patternColor',
        type: 'color',
        label: 'Pattern color',
        group: 'Background pattern',
        default: { ref: 'custom', value: '#FFFFFF' },
        visibleWhen: { field: 'patternBg', notEquals: 'none' },
    },
    {
        id: 'patternSize',
        type: 'range',
        label: 'Pattern size / spacing',
        group: 'Background pattern',
        min: 4,
        max: 80,
        step: 2,
        unit: 'px',
        default: 24,
        visibleWhen: { field: 'patternBg', notEquals: 'none' },
    },
    {
        id: 'patternThickness',
        type: 'range',
        label: 'Line / dot thickness',
        group: 'Background pattern',
        min: 1,
        max: 10,
        step: 1,
        unit: 'px',
        default: 2,
        visibleWhen: {
            field: 'patternBg',
            equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
        },
    },
    {
        id: 'patternAngle',
        type: 'range',
        label: 'Stripe angle',
        group: 'Background pattern',
        min: 0,
        max: 180,
        step: 15,
        unit: 'deg',
        default: 45,
        visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
    },
    {
        id: 'patternOpacityMode',
        type: 'select',
        label: 'Opacity type',
        group: 'Background pattern',
        default: 'low',
        options: [
            { value: 'low', label: 'Low' },
            { value: 'normal', label: 'Normal' },
        ],
        visibleWhen: { field: 'patternBg', notEquals: 'none' },
    },
    {
        id: 'patternOpacityLow',
        type: 'range',
        label: 'Opacity',
        group: 'Background pattern',
        min: 1,
        max: 20,
        step: 1,
        unit: '%',
        default: 8,
        visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
    },
    {
        id: 'patternOpacityNormal',
        type: 'range',
        label: 'Opacity',
        group: 'Background pattern',
        min: 20,
        max: 100,
        step: 5,
        unit: '%',
        default: 80,
        visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
    },
];
/** Per-block layout fields every footer block carries (Shrine `width_desktop` 1–12 + `width_mobile`). */
const WIDTH_FIELDS = [
    {
        id: 'width_desktop',
        type: 'range',
        label: 'Column width (desktop)',
        group: 'Layout',
        min: 1,
        max: 12,
        step: 1,
        default: 3,
    },
    {
        id: 'width_mobile',
        type: 'select',
        label: 'Column width (mobile)',
        group: 'Layout',
        default: '2',
        options: [
            { value: '1', label: 'Half (2 per row)' },
            { value: '2', label: 'Full (1 per row)' },
        ],
    },
];
const SOCIAL_PLATFORMS = [
    { id: 'socialInstagram', label: 'Instagram URL' },
    { id: 'socialFacebook', label: 'Facebook URL' },
    { id: 'socialWhatsapp', label: 'WhatsApp URL' },
    { id: 'socialTiktok', label: 'TikTok URL' },
    { id: 'socialYoutube', label: 'YouTube URL' },
    { id: 'socialX', label: 'X (Twitter) URL' },
    { id: 'socialPinterest', label: 'Pinterest URL' },
    { id: 'socialLinkedin', label: 'LinkedIn URL' },
];
export const footerSchema = {
    type: 'footer',
    name: 'Footer',
    version: 3,
    templates: ['footer'],
    fields: [
        // ── Appearance ──────────────────────────────────────────────────────────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Appearance',
            default: 'inverse',
            options: [
                { value: 'light', label: 'Background 1' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'accent', label: 'Accent' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        // Custom palette — only used when Color scheme = Custom. Each maps to an `--aqx-scheme-*` token the
        // footer CSS already consumes, so authoring any color here re-themes the whole footer instantly.
        {
            id: 'customBg',
            type: 'color',
            label: 'Background color',
            group: 'Appearance',
            default: { ref: 'custom', value: '#141414' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Text color',
            group: 'Appearance',
            default: { ref: 'custom', value: '#F5F5F5' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customAccent',
            type: 'color',
            label: 'Accent color',
            info: 'Newsletter button, links on hover.',
            group: 'Appearance',
            default: { ref: 'custom', value: '#97AFC5' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customBorder',
            type: 'color',
            label: 'Border color',
            info: 'Divider lines and the newsletter input outline.',
            group: 'Appearance',
            default: { ref: 'custom', value: '#333333' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'pageWidth',
            type: 'select',
            label: 'Width',
            group: 'Appearance',
            default: 'normal',
            options: [
                { value: 'narrow', label: 'Narrow' },
                { value: 'normal', label: 'Normal' },
                { value: 'full', label: 'Full width' },
            ],
        },
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 120,
            step: 4,
            unit: 'px',
            default: 48,
        },
        // ── Social links (rendered in the brand block) ────────────────────────────
        ...SOCIAL_PLATFORMS.map((p) => ({
            id: p.id,
            type: 'text',
            label: p.label,
            group: 'Social links',
            default: '',
            placeholder: 'https://…',
            maxLength: 300,
        })),
        // ── Bottom bar ────────────────────────────────────────────────────────────
        {
            id: 'showPayments',
            type: 'checkbox',
            label: 'Show payment row',
            group: 'Bottom bar',
            default: true,
        },
        // The copyright line is no longer a section field: since v3 (2026-09-21) it is the `copyright` BLOCK
        // below — a plain text the seller selects in the canvas or the rail and rewrites, like any block.
        // A v2 footer that still carries `settings.copyright` is moved onto that block at hydrate time
        // (document/hydrate.ts, migrateFooterCopyright), so nothing she wrote is lost.
        {
            id: 'brandingText',
            type: 'text',
            label: 'Branding text',
            group: 'Bottom bar',
            default: '',
            maxLength: 120,
        },
        // ── Payment methods — one switch per card (2026-10-05) ────────────────────
        // The row used to paint two generic glyphs (a card, a banknote) whatever the store accepted. It now
        // paints the brand cards buyers recognise, and the seller switches each one on or off here — the SAME
        // list and drawings as the `payment-badges` block in the buy-box and the cart drawer.
        ...paymentMethodFields('Payment methods'),
        // ── Background pattern (shared engine) ────────────────────────────────────
        ...PATTERN_FIELDS,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18).
        ...animationFields,
    ],
    blocks: [
        {
            type: 'brand_information',
            name: 'Brand information',
            max: 1,
            fields: [
                // NO default: left empty, the block signs the footer with the STORE's own name (see Component.tsx).
                // It used to default to 'aquilixShop', and `applyDefaults` STORES what it fills — so our product
                // name was written into the footer of every shop that added one.
                { id: 'heading', type: 'text', label: 'Heading', maxLength: 60 },
                {
                    id: 'text',
                    type: 'textarea',
                    label: 'Text',
                    default: 'Des produits que vous allez adorer, livrés partout au Maroc.',
                    defaultKey: 'footer.tagline',
                    rows: 3,
                    maxLength: 300,
                },
                { id: 'show_social', type: 'checkbox', label: 'Show social icons', default: true },
                ...WIDTH_FIELDS,
            ],
        },
        {
            type: 'link_list',
            name: 'Menu',
            max: 6,
            fields: [
                { id: 'heading', type: 'text', label: 'Heading', default: 'Liens', maxLength: 40 },
                {
                    id: 'menu',
                    type: 'menu',
                    label: 'Menu',
                    info: 'Pick a menu you built in Navigation. Manage menus in dashboard → Navigation.',
                    default: '',
                },
                {
                    id: 'font_size',
                    type: 'range',
                    label: 'Menu items size',
                    min: 10,
                    max: 20,
                    step: 1,
                    unit: 'px',
                    default: 14,
                },
                {
                    id: 'menu_items_font',
                    type: 'select',
                    label: 'Menu items font',
                    default: 'body',
                    options: [
                        { value: 'body', label: 'Body' },
                        { value: 'body_bold', label: 'Body — bold' },
                        { value: 'headings', label: 'Headings' },
                    ],
                },
                ...WIDTH_FIELDS,
            ],
        },
        {
            type: 'text',
            name: 'Text',
            max: 4,
            fields: [
                {
                    id: 'heading',
                    type: 'text',
                    label: 'Heading',
                    default: 'À propos',
                    defaultKey: 'footer.about',
                    maxLength: 40,
                },
                { id: 'subtext', type: 'richtext', label: 'Text' },
                ...WIDTH_FIELDS,
            ],
        },
        {
            type: 'email_signup',
            name: 'Email signup',
            max: 1,
            fields: [
                {
                    id: 'heading',
                    type: 'text',
                    label: 'Heading',
                    default: 'Restez informé',
                    defaultKey: 'newsletter.stay_informed',
                    maxLength: 60,
                },
                {
                    id: 'subtext',
                    type: 'textarea',
                    label: 'Text',
                    default: 'Inscrivez-vous pour recevoir nos nouveautés et nos offres.',
                    defaultKey: 'newsletter.invite',
                    rows: 2,
                    maxLength: 200,
                },
                {
                    id: 'button_label',
                    type: 'text',
                    label: 'Button label',
                    default: 'S’abonner',
                    maxLength: 32,
                },
                ...WIDTH_FIELDS,
            ],
        },
        {
            type: 'image',
            name: 'Image',
            max: 2,
            fields: [
                { id: 'image', type: 'image', label: 'Image' },
                {
                    id: 'image_width',
                    type: 'range',
                    label: 'Image width',
                    min: 50,
                    max: 400,
                    step: 5,
                    unit: 'px',
                    default: 120,
                },
                {
                    id: 'alignment',
                    type: 'select',
                    label: 'Alignment',
                    default: 'left',
                    options: [
                        { value: 'left', label: 'Left' },
                        { value: 'center', label: 'Center' },
                        { value: 'right', label: 'Right' },
                    ],
                },
                { id: 'url', type: 'link', label: 'Link', default: undefined },
                ...WIDTH_FIELDS,
            ],
        },
        {
            // The bottom-bar copyright line. ONE block, rendered under the block grid next to the payment row —
            // never in the grid. `{year}` is substituted at render. It starts as the bare year: a language-
            // neutral default that needs no dictionary, and the seller types her own name after it.
            type: 'copyright',
            name: 'Copyright',
            max: 1,
            fields: [
                {
                    id: 'copyright',
                    type: 'text',
                    label: 'Copyright',
                    info: 'Use {year} to insert the current year automatically.',
                    default: '© {year}',
                    maxLength: 160,
                },
            ],
        },
    ],
    maxBlocks: 13,
    migrate(fromVersion, settings) {
        // v1→v2: top-level fields colorScheme / showPayments / copyright / pattern* keep their ids and survive
        // unchanged; the new fields hydrate to their defaults. Legacy `storeName`/`tagline`/`showNewsletter`/
        // `newsletterHeading` are left in place (ignored), and legacy `column`/`social` BLOCKS still render via
        // the Component's back-compat path. Nothing to transform — a passthrough keeps old footers intact.
        // v2→v3: `copyright` left the section fields for a block of its own. That move touches BLOCKS, which
        // this settings-only hook cannot see — hydrate.ts (migrateFooterCopyright) does it on every hydrate,
        // builder and storefront alike. Settings themselves pass through unchanged.
        if (fromVersion >= 2)
            return settings;
        return settings;
    },
};
export const footerPresets = [
    {
        name: 'Footer',
        settings: {
            colorScheme: 'inverse',
            pageWidth: 'normal',
            showPayments: true,
            // `copyright` was repeated here, identical to the field's own default — a preset is spread OVER the
            // defaults, so the copy would have put French back. `@t:` marks a sentence to translate at insert.
            brandingText: '',
        },
        blocks: [
            {
                type: 'brand_information',
                settings: {
                    // No `heading` — same reason as the field: the shop's own name is the only right signature.
                    text: '@t:footer.tagline',
                    show_social: true,
                    width_desktop: 4,
                    width_mobile: '2',
                },
            },
            {
                type: 'link_list',
                settings: {
                    heading: '@t:common.shop',
                    menu: 'main-menu',
                    width_desktop: 3,
                    width_mobile: '1',
                },
            },
            {
                type: 'link_list',
                settings: {
                    heading: '@t:footer.help',
                    menu: 'footer',
                    width_desktop: 3,
                    width_mobile: '1',
                },
            },
            {
                type: 'email_signup',
                settings: {
                    button_label: '@t:newsletter.subscribe',
                    width_desktop: 2,
                    width_mobile: '2',
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const footerMeta = {
    label: 'Footer',
    group: 'Footer',
    icon: 'globe',
};
//# sourceMappingURL=schema.js.map